/**
 * Ted Item 34 (HTHD, 10-07: "extremely important") — print the run card AT
 * check-in, without going looking for a button.
 *
 * Ted looked through Front Desk, Bookings, Schedule, the day list and
 * Lodging and could not find a run card: the only "Print Run Cards" button
 * lived on Bookings, for the whole day's boarders. This hook is the one
 * prompt every check-in surface (Front Desk, Dashboard arrivals, Bookings)
 * shows after a check-in SUCCEEDS:
 *
 *   "Rufus is checked in. Print the run card?"  [Not now]  [Print run card]
 *
 * Ordering is the safety argument, same as the #716 capture sheet: it is
 * offered only after the server accepted the check-in, so dismissing it,
 * losing the network or closing the tab all leave the dog correctly checked
 * in. Printing is one click (the primary button has focus, so Enter prints);
 * Escape or "Not now" dismisses.
 *
 * A household check-in offers ONE prompt for all its dogs (one card per
 * dog, printed together). A dog confirmed later in the same flow (a
 * transported dog's arrival question) joins the open prompt instead of
 * stacking a second one.
 *
 * Ted #46 (#1218) — "Edit card" on the prompt opens the run-card editor IN
 * the same panel (never a dialog on a dialog). Saving writes the edits to
 * the stay and reloads the cards from the server, so what prints is what the
 * server will reprint, not a client-side guess. Every print (the prompt's,
 * and the day deck's) is reported back so the visit keeps the card as
 * printed; a reprint of a visit whose profile has since changed offers
 * "Print today's version".
 *
 * Sawyer, 10-30: "I don't need the check in box for run cards when I check in
 * for daycare." The server says per card whether its service asks at check-in
 * (`prompt_on_check_in`, owner-set in Settings → Operations, daycare and
 * half-day off by default), so one rule covers every surface and a family with
 * a boarder and a daycare dog is asked about the boarder only. The day's deck
 * is boarders only, so `printCards` is the by-hand way to a daycare dog's card:
 * the same dialog, whatever the setting says.
 *
 * Deploy-order safety: the frontend ships on merge and the server does not.
 * A server older than Item 34 ignores `booking_ids` and answers with the
 * day's boarding deck, so the cards are filtered by booking id here; a
 * daycare dog then simply gets no prompt until the server deploy lands.
 * A server older than Sawyer's change omits `prompt_on_check_in`: the prompt
 * asks, as it always did, until that deploy lands.
 * No card ⇒ no prompt, never an empty one.
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  fetchRunCards,
  humanizeApiError,
  recordRunCardsPrinted,
  todayIsoDate,
} from '../../lib/api.js';
import { RunCardPrintLayout } from './RunCardPrintLayout.js';
import type { RunCardData } from './RunCard.js';
import { RunCardEditor } from './RunCardEditor.js';
import { apiCardToRunCard } from './api-card-to-run-card.js';
import { formatAllergies } from './run-card-grid.js';
import { joinDogNames } from '../dashboard/household-checkin.js';

type PromptState =
  | { readonly kind: 'idle' }
  | {
      readonly kind: 'asking';
      readonly cards: ReadonlyArray<RunCardData>;
      /** Ted #46 — the card whose editor is open in the prompt, or null. */
      readonly editing: string | null;
    }
  | {
      readonly kind: 'failed';
      readonly bookingIds: ReadonlyArray<string>;
      /** The desk asked by hand: a retry must not fall back to the check-in rule. */
      readonly byHand: boolean;
    }
  | {
      readonly kind: 'printing';
      readonly cards: ReadonlyArray<RunCardData>;
      /** Straight to the OS print dialog (the prompt's one click), or a preview first (the day deck). */
      readonly autoPrint: boolean;
      /** The bookings this deck was loaded for; null = the whole day. */
      readonly bookingIds: ReadonlyArray<string> | null;
      /** Ted #46 — loaded as today's version (`live`), so printing re-records. */
      readonly live: boolean;
    };

export interface UseRunCardPromptOptions {
  readonly apiUrl: string;
  readonly token: string | null;
  /** `config.features?.runCardPromptOnCheckIn !== false` — the tenant's opt-out. */
  readonly enabled: boolean;
}

export interface RunCardPrompt {
  /** Call after a check-in succeeds, with the booking ids that went in. */
  readonly offer: (bookingIds: ReadonlyArray<string>) => void;
  /**
   * Sawyer 10-30 — the same dialog, asked for by hand from a checked-in dog's
   * row. Ignores the per-service setting and the tenant's opt-out: those only
   * decide whether a check-in ASKS.
   */
  readonly printCards: (bookingIds: ReadonlyArray<string>) => void;
  /** The day's deck (every boarder on site today), as a print preview — the button for the desk. */
  readonly printToday: () => void;
  /** True while the day's deck is loading (the desk button shows it). */
  readonly loadingToday: boolean;
  /** Render this once, anywhere in the page. Null when there is nothing to show. */
  readonly element: JSX.Element | null;
  /**
   * Sawyer #1296 — true while a check-in's cards are loading or the prompt /
   * preview is open, so another post-check-in prompt (the owner's-notes
   * question) waits its turn instead of stacking a dialog on this one.
   */
  readonly active: boolean;
}

function mergeCards(
  current: ReadonlyArray<RunCardData>,
  incoming: ReadonlyArray<RunCardData>,
): RunCardData[] {
  const seen = new Set(current.map((c) => c.cardId));
  return [...current, ...incoming.filter((c) => !seen.has(c.cardId))];
}

function todayLabel(): string {
  return new Date(`${todayIsoDate()}T00:00:00`).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
}

export function useRunCardPrompt({
  apiUrl,
  token,
  enabled,
}: UseRunCardPromptOptions): RunCardPrompt {
  const [state, setState] = useState<PromptState>({ kind: 'idle' });
  const [loadingToday, setLoadingToday] = useState(false);
  const [todayError, setTodayError] = useState<string | null>(null);
  // Sawyer #1296 — offers whose cards are still loading (see `active`).
  const [offersLoading, setOffersLoading] = useState(0);
  // A check-in can resolve after the page unmounted (navigate away mid-POST).
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  /**
   * Ted #46 — tell the server these cards were printed, so each visit keeps
   * the card as printed. Best-effort on purpose: the paper is already
   * coming out of the printer, and a failed record only means the next
   * reprint shows today's card instead of this one.
   */
  const recordPrinted = useCallback(
    (cards: ReadonlyArray<RunCardData>, live: boolean) => {
      if (!token) return;
      void recordRunCardsPrinted(apiUrl, token, cards, { refresh: live }).catch(() => undefined);
    },
    [apiUrl, token],
  );

  /** The cards for these bookings, fresh from the server (ids filtered: see header). */
  const loadCards = useCallback(
    async (
      bookingIds: ReadonlyArray<string>,
      opts: { readonly live?: boolean; readonly checkInOnly?: boolean } = {},
    ): Promise<RunCardData[]> => {
      if (!token) return [];
      const wanted = new Set(bookingIds);
      const res = await fetchRunCards(apiUrl, token, todayIsoDate(), {
        bookingIds: [...wanted],
        live: opts.live ?? false,
      });
      const cards = Array.isArray(res.cards) ? res.cards : [];
      return cards
        .filter((c) => wanted.has(c.booking_id))
        // `!== false`: an older server omits the key, and then the prompt asks.
        .filter((c) => !opts.checkInOnly || c.prompt_on_check_in !== false)
        .map(apiCardToRunCard);
    },
    [apiUrl, token],
  );

  const present = useCallback(
    (bookingIds: ReadonlyArray<string>, byHand: boolean) => {
      const wanted = new Set(bookingIds);
      setOffersLoading((n) => n + 1);
      void (async () => {
        try {
          const mine = await loadCards([...wanted], { checkInOnly: !byHand });
          if (!mounted.current) return;
          if (mine.length === 0) {
            // Asked by hand, silence would read as a dead button.
            if (byHand) setTodayError('There is no run card for this visit today.');
            return;
          }
          setState((prev) =>
            prev.kind === 'asking'
              ? { ...prev, cards: mergeCards(prev.cards, mine) }
              : prev.kind === 'printing'
                ? prev // never yank the print preview out from under the desk
                : { kind: 'asking', cards: mine, editing: null },
          );
        } catch {
          if (!mounted.current) return;
          // The dog IS checked in; only the card failed to load. Say so,
          // with a retry, rather than silently skipping the prompt.
          setState((prev) =>
            prev.kind === 'idle' || prev.kind === 'failed'
              ? { kind: 'failed', bookingIds: [...wanted], byHand }
              : prev,
          );
        } finally {
          if (mounted.current) setOffersLoading((n) => Math.max(0, n - 1));
        }
      })();
    },
    [loadCards],
  );

  const offer = useCallback(
    (bookingIds: ReadonlyArray<string>) => {
      if (!enabled || !token || bookingIds.length === 0) return;
      present(bookingIds, false);
    },
    [enabled, token, present],
  );

  const printCards = useCallback(
    (bookingIds: ReadonlyArray<string>) => {
      if (!token || bookingIds.length === 0) return;
      setTodayError(null);
      present(bookingIds, true);
    },
    [token, present],
  );

  const printToday = useCallback(() => {
    if (!token) return;
    setLoadingToday(true);
    setTodayError(null);
    void (async () => {
      try {
        const res = await fetchRunCards(apiUrl, token, todayIsoDate());
        if (!mounted.current) return;
        const cards = Array.isArray(res.cards) ? res.cards : [];
        setState({
          kind: 'printing',
          cards: cards.map(apiCardToRunCard),
          autoPrint: false,
          bookingIds: null,
          live: false,
        });
      } catch (err) {
        if (!mounted.current) return;
        setTodayError(
          err instanceof Error ? humanizeApiError(err.message) : 'Could not load run cards',
        );
      } finally {
        if (mounted.current) setLoadingToday(false);
      }
    })();
  }, [apiUrl, token]);

  const close = useCallback(() => setState({ kind: 'idle' }), []);

  /** Ted #46 — "Print today's version": reload this deck as `live`. */
  const loadLiveDeck = useCallback(
    (prev: Extract<PromptState, { kind: 'printing' }>) => {
      if (!token) return;
      void (async () => {
        try {
          const cards = prev.bookingIds
            ? await loadCards(prev.bookingIds, { live: true })
            : (await fetchRunCards(apiUrl, token, todayIsoDate(), { live: true })).cards.map(
                apiCardToRunCard,
              );
          if (!mounted.current) return;
          setState({ ...prev, cards, live: true, autoPrint: false });
        } catch (err) {
          if (!mounted.current) return;
          setTodayError(
            err instanceof Error ? humanizeApiError(err.message) : 'Could not load run cards',
          );
        }
      })();
    },
    [apiUrl, token, loadCards],
  );

  let element: JSX.Element | null = null;
  if (state.kind === 'asking') {
    const bookingIds = [...new Set(state.cards.map((c) => c.bookingId))];
    const editingCard = state.editing
      ? state.cards.find((c) => c.cardId === state.editing) ?? null
      : null;
    element = (
      <RunCardPromptDialog
        cards={state.cards}
        onPrint={() =>
          setState({
            kind: 'printing',
            cards: state.cards,
            autoPrint: true,
            bookingIds,
            live: false,
          })
        }
        onClose={close}
        onEdit={
          token
            ? (cardId) => setState({ ...state, editing: cardId })
            : undefined
        }
        editor={
          editingCard && token ? (
            <RunCardEditor
              apiUrl={apiUrl}
              token={token}
              bookingId={editingCard.bookingId}
              dogId={editingCard.dogId}
              dogName={editingCard.dogName}
              onBack={() => setState({ ...state, editing: null })}
              onSaved={async () => {
                // Reload from the server: the card that prints is the one
                // the server now holds for this stay. A failure here rejects
                // into the editor's error state (the edits ARE saved).
                const fresh = await loadCards(bookingIds);
                if (!mounted.current) return;
                setState({ kind: 'asking', cards: fresh.length > 0 ? fresh : state.cards, editing: null });
              }}
            />
          ) : null
        }
      />
    );
  } else if (state.kind === 'failed') {
    const { bookingIds: ids, byHand } = state;
    element = (
      <RunCardPromptFailed
        onRetry={() => {
          setState({ kind: 'idle' });
          present(ids, byHand);
        }}
        onClose={close}
      />
    );
  } else if (state.kind === 'printing') {
    element = (
      <RunCardPrintLayout
        cards={state.cards}
        dateLabel={todayLabel()}
        onClose={close}
        autoPrint={state.autoPrint}
        onPrint={() => recordPrinted(state.cards, state.live)}
        onUseLive={() => loadLiveDeck(state)}
      />
    );
  } else if (todayError) {
    element = (
      <div className="ferro-dashboard__error" role="alert" data-testid="run-cards-today-error">
        <p>{todayError}</p>
        <button
          type="button"
          className="ferro-btn ferro-btn--ghost"
          onClick={() => setTodayError(null)}
        >
          Dismiss
        </button>
      </div>
    );
  }

  return { offer, printCards, printToday, loadingToday, element, active: state.kind !== 'idle' || offersLoading > 0 };
}

interface RunCardPromptDialogProps {
  readonly cards: ReadonlyArray<RunCardData>;
  readonly onPrint: () => void;
  readonly onClose: () => void;
  /** Ted #46 — open the editor for one card. Absent ⇒ no Edit buttons. */
  readonly onEdit?: (cardId: string) => void;
  /** Ted #46 — when set, the panel shows this editor instead of the list. */
  readonly editor?: JSX.Element | null;
}

/** Whether the desk has changed anything on this card for this stay. */
export function cardHasStayEdits(c: RunCardData): boolean {
  const e = c.stayEdits;
  return Boolean(
    (e && ((e.note && e.note.trim().length > 0) || e.fields.length > 0 || e.overridden.length > 0)) ||
      (c.feedingOverride && c.feedingOverride.trim().length > 0),
  );
}

/** The prompt itself. Presentational; exported for the unit test. */
export function RunCardPromptDialog({
  cards,
  onPrint,
  onClose,
  onEdit,
  editor,
}: RunCardPromptDialogProps): JSX.Element {
  const titleId = useId();
  const printRef = useRef<HTMLButtonElement>(null);
  const editing = Boolean(editor);
  useEffect(() => {
    // Focus the print button: printing is the one-click (or one-Enter) path
    // — including on the way back from the editor.
    if (!editing) printRef.current?.focus();
  }, [editing]);
  if (editor) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Edit run card"
        data-testid="run-card-prompt"
        data-mode="editing"
        className="ferro-blocking-dialog__overlay"
      >
        <div className="ferro-blocking-dialog__panel ferro-blocking-dialog__panel--editor">{editor}</div>
      </div>
    );
  }
  const names = cards.map((c) => c.dogName);
  const one = cards.length === 1;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="run-card-prompt"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div className="ferro-blocking-dialog__panel">
        <h2 id={titleId}>{one ? 'Print the run card?' : `Print ${cards.length} run cards?`}</h2>
        <p data-testid="run-card-prompt-body">
          {joinDogNames(names)} {one ? 'is' : 'are'} checked in.{' '}
          {one ? 'Print the run card for the kennel.' : 'One card per dog, printed together.'}
        </p>
        <ul className="ferro-sibling-prompt__list" data-testid="run-card-prompt-list">
          {cards.map((c) => {
            const allergies = formatAllergies(c.allergies);
            return (
              <li key={c.cardId} className="ferro-sibling-prompt__item">
                <div className="ferro-run-card-prompt__row">
                  <span className="ferro-sibling-prompt__dog">{c.dogName}</span>
                  <span className="ferro-sibling-prompt__meta">{c.serviceLabel}</span>
                  {cardHasStayEdits(c) ? (
                    <span
                      className="ferro-run-card-prompt__edited"
                      data-testid={`run-card-prompt-edited-${c.cardId}`}
                    >
                      Edited for this stay
                    </span>
                  ) : null}
                  {onEdit ? (
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-run-card-prompt__edit"
                      onClick={() => onEdit(c.cardId)}
                      aria-label={`Edit ${c.dogName}’s run card`}
                      data-testid={`run-card-prompt-edit-${c.cardId}`}
                    >
                      Edit card
                    </button>
                  ) : null}
                </div>
                {allergies ? (
                  <p
                    className="ferro-run-card-prompt__allergies"
                    data-testid={`run-card-prompt-allergies-${c.cardId}`}
                  >
                    <strong>⚠ Allergies:</strong> {allergies}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
        <div className="ferro-blocking-dialog__actions">
          <button
            type="button"
            className="ferro-btn ferro-btn--ghost"
            onClick={onClose}
            data-testid="run-card-prompt-skip"
          >
            Not now
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            ref={printRef}
            onClick={onPrint}
            data-testid="run-card-prompt-print"
          >
            {one ? 'Print run card' : `Print ${cards.length} run cards`}
          </button>
        </div>
      </div>
    </div>
  );
}

function RunCardPromptFailed({
  onRetry,
  onClose,
}: {
  readonly onRetry: () => void;
  readonly onClose: () => void;
}): JSX.Element {
  const titleId = useId();
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      data-testid="run-card-prompt-failed"
      className="ferro-blocking-dialog__overlay"
      onKeyDown={(event) => {
        if (event.key !== 'Escape') return;
        event.stopPropagation();
        onClose();
      }}
    >
      <div className="ferro-blocking-dialog__panel">
        <h2 id={titleId}>The run card didn&apos;t load</h2>
        <p>The check-in went through. Only the run card couldn&apos;t be fetched.</p>
        <div className="ferro-blocking-dialog__actions">
          <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onClose}>
            Not now
          </button>
          <button
            type="button"
            className="ferro-btn ferro-btn--primary"
            onClick={onRetry}
            data-testid="run-card-prompt-retry"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  );
}
